import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { 
  Wallet, 
  Search, 
  Menu, 
  X, 
  ChevronDown, 
  ChevronRight,
  Activity, 
  BookOpen, 
  HelpCircle,
  Landmark,
  Coins,
  HeartHandshake,
  Receipt,
  Briefcase,
  Building,
  ShieldAlert,
  Shield
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { allTools, getSlug } from "../data/allTools";
import { categories } from "../data/categories";
import ThemeSwitcher from "./ThemeSwitcher";

const getCategoryIcon = (id: string, className = "h-4 w-4") => {
  switch (id) {
    case "loans":
      return <Landmark className={className} />;
    case "savings":
      return <Coins className={className} />;
    case "retirement":
      return <HeartHandshake className={className} />;
    case "budget":
      return <Wallet className={className} />;
    case "tax":
      return <Receipt className={className} />;
    case "income":
      return <Briefcase className={className} />;
    case "banking":
      return <Building className={className} />;
    case "networth":
      return <ShieldAlert className={className} />;
    case "insurance":
      return <Shield className={className} />;
    default:
      return <Activity className={className} />;
  }
};

interface HeaderProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

export default function Header({
  searchQuery,
  setSearchQuery,
}: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCalculatorsOpen, setIsCalculatorsOpen] = useState(false);
  const [isMobileCalculatorsOpen, setIsMobileCalculatorsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(categories[0]?.id || "loans");
  const navigate = useNavigate();
  const location = useLocation();

  const handleHomeClick = (e: React.MouseEvent) => {
    setIsCalculatorsOpen(false);
    if (location.pathname === "/") {
      e.preventDefault();
      setSearchQuery("");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setSearchQuery("");
      setIsMobileMenuOpen(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (value.trim() !== "") {
      setIsCalculatorsOpen(false);
      if (location.pathname !== "/") {
        navigate("/");
      }
      setTimeout(() => {
        const el = document.getElementById("calculator-directory-title");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 120);
    }
  };

  const handleCalculatorClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setSearchQuery("");
    setIsCalculatorsOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
    }
    setTimeout(() => {
      const el = document.getElementById("calculator-directory-title");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 120);
    setIsMobileMenuOpen(false);
  };

  const handleFeaturesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsCalculatorsOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
    }
    setTimeout(() => {
      const el = document.getElementById("features-section");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 120);
    setIsMobileMenuOpen(false);
  };

  const handleFAQClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsCalculatorsOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
    }
    setTimeout(() => {
      const el = document.getElementById("faq-section");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 120);
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-800/80 bg-slate-950/75 backdrop-blur-md relative">
      {isCalculatorsOpen && (
        <div 
          className="fixed inset-0 z-40 bg-transparent" 
          onClick={() => setIsCalculatorsOpen(false)}
        />
      )}
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8 relative z-50">
        
        {/* LEFT SECTION: BRANDING & LOGO */}
        <Link 
          to="/" 
          onClick={handleHomeClick} 
          className="flex items-center gap-4 cursor-pointer select-none no-underline group"
        >
          {/* Glowing Premium Logo Badge */}
          <div className="relative flex h-11 w-11 items-center justify-center rounded-xl border border-amber-500/30 bg-gradient-to-br from-slate-900 to-slate-950 shadow-[0_0_15px_rgba(245,158,11,0.12)] transition-all duration-300 group-hover:border-amber-400/50">
            <Wallet className="h-5 w-5 text-amber-500 animate-none group-hover:scale-105 transition-transform" />
            <div className="absolute -inset-0.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 opacity-10 blur phase-2000 animate-pulse"></div>
          </div>

          {/* Brand Typography */}
          <div className="flex flex-col text-left">
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold tracking-tight text-white sm:text-2xl">
                USMoney<span className="text-amber-400">AI</span>
              </span>
              {/* Active Verification Status Dot */}
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
            </div>

          </div>
        </Link>

        {/* MIDDLE SECTION: PROFESSIONAL NAVIGATION MENU */}
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-300">
          <Link 
            to="/" 
            onClick={handleHomeClick}
            className="transition-colors hover:text-white text-slate-300 font-medium no-underline"
          >
            Home
          </Link>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              setIsCalculatorsOpen(!isCalculatorsOpen);
            }} 
            className="transition-colors hover:text-white flex items-center gap-1 cursor-pointer bg-transparent border-none text-slate-300 font-medium text-sm focus:outline-none"
          >
            Calculators <ChevronDown className={`h-3.5 w-3.5 opacity-60 transition-transform duration-250 ${isCalculatorsOpen ? "rotate-180 text-amber-400" : ""}`} />
          </button>
          <Link 
            to="/blog" 
            className="transition-colors hover:text-white cursor-pointer text-slate-300 font-medium text-sm no-underline flex items-center gap-1.5"
          >
            Financial Guides
          </Link>
          <button 
            onClick={handleFeaturesClick} 
            className="transition-colors hover:text-white cursor-pointer bg-transparent border-none text-slate-300 font-medium text-sm"
          >
            Resources
          </button>
          <button 
            onClick={handleFAQClick} 
            className="transition-colors hover:text-white cursor-pointer bg-transparent border-none text-slate-300 font-medium text-sm"
          >
            FAQ
          </button>
        </nav>

        {/* RIGHT SECTION: SEARCH & QUICK CTA */}
        <div className="hidden md:flex items-center gap-4">
          {/* Sleek Minimalist Search Bar */}
          <div className="relative flex h-9 items-center rounded-lg border border-slate-800 bg-slate-900/50 px-3 transition-all hover:border-slate-700">
            <Search className="h-3.5 w-3.5 text-slate-400 shrink-0 mr-2" />
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search calculators..."
              className="bg-transparent text-xs text-white placeholder-slate-400 focus:outline-none w-36 focus:w-48 transition-all duration-300"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery("")} 
                className="ml-1 text-slate-500 hover:text-slate-300 font-bold text-xs bg-transparent border-none cursor-pointer"
              >
                ✕
              </button>
            )}
          </div>

          {/* Theme Mode Switcher */}
          <ThemeSwitcher />

          {/* Premium Glowing Explore Calculators Button */}
          <button 
            onClick={handleCalculatorClick} 
            className="relative group overflow-hidden rounded-xl p-[1px] focus:outline-none cursor-pointer bg-transparent border-none"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-amber-500 via-amber-600 to-amber-500 bg-[length:200%_auto] bg-amber-500 animate-pulse opacity-70 group-hover:opacity-100 transition-opacity duration-300"></span>
            <span className="relative flex items-center gap-1.5 rounded-xl bg-slate-950 px-4 py-2 text-xs font-semibold text-white transition-all duration-200 group-hover:bg-slate-900">
              <Activity className="h-3.5 w-3.5 text-amber-400 group-hover:animate-pulse" />
              Explore Calculators
            </span>
          </button>
        </div>

        {/* MOBILE MENU TRIGGER BUTTON */}
        <div className="flex md:hidden">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="inline-flex items-center justify-center rounded-lg p-2 text-slate-400 hover:bg-slate-900 hover:text-white cursor-pointer bg-transparent border-none"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* MOBILE RESPONSIVE DRAWER */}
      {isMobileMenuOpen && (
        <div className="border-b border-slate-900 bg-slate-950 px-4 pt-2 pb-6 md:hidden animate-fade-in text-left">
          <div className="space-y-3">
            <Link 
              to="/" 
              onClick={handleHomeClick}
              className="w-full text-left block rounded-lg px-3 py-2 text-base font-medium text-slate-300 hover:bg-slate-900 hover:text-white no-underline"
            >
              Home
            </Link>
            <button 
              onClick={() => setIsMobileCalculatorsOpen(!isMobileCalculatorsOpen)} 
              className="w-full text-left flex items-center justify-between rounded-lg px-3 py-2 text-base font-medium text-slate-300 hover:bg-slate-900 hover:text-white cursor-pointer bg-transparent border-none focus:outline-none"
            >
              <span>Calculators</span>
              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isMobileCalculatorsOpen ? "rotate-180 text-amber-400" : ""}`} />
            </button>

            {isMobileCalculatorsOpen && (
              <div className="pl-4 pr-1 py-2 space-y-3 max-h-[300px] overflow-y-auto rounded-xl bg-slate-900/20 border border-slate-900/60 text-left">
                {categories.map((cat) => (
                  <div key={cat.id} className="space-y-1">
                    <div className="text-xs font-bold text-slate-500 px-2 py-1 flex items-center gap-1.5 uppercase tracking-wider">
                      {getCategoryIcon(cat.id, "h-3.5 w-3.5 text-amber-500/80")}
                      {cat.name}
                    </div>
                    <div className="grid grid-cols-1 gap-1 pl-3 border-l border-slate-800/80">
                      {allTools
                        .filter(tool => tool.category === cat.id)
                        .map(tool => {
                          const slug = getSlug(tool.name);
                          return (
                            <Link
                              key={tool.id}
                              to={`/${slug}`}
                              onClick={() => {
                                setIsMobileCalculatorsOpen(false);
                                setIsMobileMenuOpen(false);
                              }}
                              className="text-xs text-slate-450 hover:text-white py-1 block no-underline transition-colors leading-tight hover:pl-1 text-left"
                            >
                              {tool.name}
                            </Link>
                          );
                        })}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Link 
              to="/blog" 
              onClick={() => setIsMobileMenuOpen(false)}
              className="w-full text-left block rounded-lg px-3 py-2 text-base font-medium text-slate-300 hover:bg-slate-900 hover:text-white no-underline flex items-center gap-1.5"
            >
              <BookOpen className="h-4 w-4 text-amber-500" />
              Financial Guides
            </Link>
            <button 
              onClick={handleFeaturesClick} 
              className="w-full text-left block rounded-lg px-3 py-2 text-base font-medium text-slate-300 hover:bg-slate-900 hover:text-white cursor-pointer bg-transparent border-none"
            >
              Resources
            </button>
            <button 
              onClick={handleFAQClick} 
              className="w-full text-left block rounded-lg px-3 py-2 text-base font-medium text-slate-300 hover:bg-slate-900 hover:text-white cursor-pointer bg-transparent border-none"
            >
              FAQ
            </button>
            
            <div className="pt-4 border-t border-slate-900 flex flex-col gap-3">
              <div className="flex items-center justify-between px-3 py-1">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Appearance</span>
                <ThemeSwitcher />
              </div>
              <div className="relative">
                <Search className="absolute top-3 left-3 h-4 w-4 text-slate-500" />
                <input 
                  type="text" 
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search calculators..." 
                  className="w-full rounded-lg border border-slate-800 bg-slate-900/50 py-2 pr-4 pl-10 text-sm text-white placeholder-slate-500 focus:border-amber-500 focus:outline-none focus:ring-0"
                />
              </div>
              <button 
                onClick={handleCalculatorClick} 
                className="w-full justify-center flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 py-2.5 text-sm font-semibold text-slate-950 cursor-pointer border-none"
              >
                Explore Calculators
              </button>
            </div>
          </div>
        </div>
      )}
      {/* MEGA MENU DROPDOWN */}
      <AnimatePresence>
        {isCalculatorsOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="absolute top-full left-0 right-0 w-full border-b border-slate-800/80 bg-slate-950/98 backdrop-blur-xl shadow-[0_25px_60px_rgba(0,0,0,0.9)] z-50 hidden md:block"
          >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-12 gap-8 h-[480px]">
              {/* Left Panel: Categories */}
              <div className="col-span-4 border-r border-slate-800/50 pr-6 overflow-y-auto max-h-[416px] space-y-1">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest px-3 mb-3">
                  Categories
                </div>
                {categories.map((cat) => {
                  const isActive = activeCategory === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onMouseEnter={() => setActiveCategory(cat.id)}
                      onClick={() => setActiveCategory(cat.id)}
                      className={`w-full flex items-start gap-4 p-3 rounded-xl transition-all text-left border border-transparent cursor-pointer group focus:outline-none ${
                        isActive 
                          ? "bg-amber-400/10 border-amber-500/20 text-amber-400" 
                          : "bg-transparent text-slate-400 hover:bg-slate-900/40 hover:text-slate-200"
                      }`}
                    >
                      <span className={`p-1.5 rounded-lg shrink-0 transition-colors ${isActive ? "text-amber-400 bg-amber-500/10" : "text-slate-500 bg-slate-900 group-hover:text-amber-450"}`}>
                        {getCategoryIcon(cat.id, "h-4 w-4")}
                      </span>
                      <div className="flex flex-col min-w-0">
                        <span className="font-semibold text-sm leading-tight">{cat.name}</span>
                        <span className="text-[11px] text-slate-500 mt-1 line-clamp-2 leading-relaxed font-light group-hover:text-slate-400">
                          {cat.description}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Right Panel: Calculators Grid */}
              <div className="col-span-8 flex flex-col h-full pl-2">
                <div className="flex items-center justify-between border-b border-slate-900 pb-3 mb-4">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                    {categories.find(c => c.id === activeCategory)?.name || "Calculators"} 
                    <span className="text-amber-500/80 font-medium ml-2 text-[11px] normal-case bg-amber-500/5 px-2 py-0.5 rounded-full border border-amber-500/10">
                      {allTools.filter(t => t.category === activeCategory).length} Tools Available
                    </span>
                  </div>
                  
                  <Link 
                    to="/" 
                    onClick={handleCalculatorClick} 
                    className="text-xs text-amber-500 hover:text-amber-400 font-medium hover:underline flex items-center gap-1.5 no-underline"
                  >
                    View Directory Card Grid <ChevronRight className="h-3 w-3" />
                  </Link>
                </div>

                <div className="overflow-y-auto pr-2 max-h-[352px] grid grid-cols-1 sm:grid-cols-2 gap-3.5 pb-6">
                  {allTools
                    .filter((tool) => tool.category === activeCategory)
                    .map((tool) => {
                      const slug = getSlug(tool.name);
                      return (
                        <Link
                          key={tool.id}
                          to={`/${slug}`}
                          onClick={() => setIsCalculatorsOpen(false)}
                          className="flex flex-col p-4 rounded-xl border border-slate-900/60 bg-slate-900/10 hover:bg-slate-900/40 hover:border-slate-800 text-left transition-all hover:shadow-[0_4px_20px_rgba(0,0,0,0.4)] group no-underline min-h-[96px] justify-between"
                        >
                          <div className="flex items-center justify-between gap-2 min-w-0">
                            <span className="font-semibold text-sm text-slate-200 group-hover:text-amber-400 transition-colors truncate">
                              {tool.name}
                            </span>
                            <ChevronRight className="h-3.5 w-3.5 text-slate-600 opacity-0 group-hover:opacity-100 group-hover:text-amber-400 transition-all shrink-0 transform translate-x-1" />
                          </div>
                          <span className="text-xs text-slate-500 mt-1.5 line-clamp-2 leading-relaxed font-light group-hover:text-slate-400">
                            {tool.description}
                          </span>
                        </Link>
                      );
                    })}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
